import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIVITY_QUIET, activityIndicator, activitySaid, type ActivityItem, type ActivityResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Logo } from "./logo.js";
import { ACTIVITY_KEY } from "./queryClient.js";

/**
 * What Joogoos is doing for this business, in the header ([27](../../../../docs/27-what-joogoos-is-doing.md), ADR 0045).
 *
 * **Nothing is drawn while nothing is happening.** An indicator that is always there is furniture, and furniture
 * stops being read — which is the whole point of the one on the day somebody is waiting and wondering. Otherwise a
 * spinner and "Working on 3", the waiting dot and "2 waiting", or a red "1 stopped"; pressing it shows what each of
 * them is, in the same words the screen it belongs to uses, and a click goes to that screen.
 *
 * It is one read, asked every five seconds while anything is happening and **not at all** once it has finished
 * ([17](../../../../docs/17-security-and-performance.md) §performance 4): a tab left alone goes quiet. It is asked
 * again by a write that succeeded (`queryClient.ts`) and by coming back to the tab, which between them cover
 * everything this browser starts.
 */
export const useActivity = () =>
  useQuery({
    queryKey: ACTIVITY_KEY, queryFn: api.activity, refetchInterval: api.whileWorking, staleTime: 2_000,
    // The one query in the app that wants this: coming back to a tab is exactly when somebody wonders what happened.
    refetchOnWindowFocus: true,
  });

/**
 * When a piece of work leaves the list, or stops, what is on the screen is read again (2026-09-30).
 *
 * The indicator stopped spinning the moment a post reached Instagram, and the Calendar beside it still said
 * *Scheduled* until somebody reloaded: the screen that owns the work had no way to know it had moved, because nothing
 * but this read watches it. One rule here rather than a poll on every screen that has something going — the same shape
 * as a write re-asking this read (`queryClient.ts`) — so a finished post, article, lookup or export shows on its own
 * screen, and the balance beside it says what it cost. Only the queries on the screen are asked again (TanStack's
 * `refetchType: "active"`); the rest are marked stale and read when they are next opened.
 */
function useFinishedWorkRefreshes(view: ActivityResponse | undefined) {
  const qc = useQueryClient();
  const was = useRef<Map<string, ActivityItem["state"]>>(new Map());
  useEffect(() => {
    if (!view) return;
    const now = new Map(view.items.map((i) => [i.id, i.state]));
    const moved = [...was.current].some(([id, state]) => state !== "stopped" && now.get(id) !== state);
    was.current = now;
    if (moved) void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== ACTIVITY_KEY[0] });
  }, [view, qc]);
}

export function Activity({ onGo }: { onGo: (item: ActivityItem) => void }) {
  // The same read the header's mark crunches on: two observers of one key are one request (17 §performance 4).
  const read = useActivity().data;
  useFinishedWorkRefreshes(read);
  const view: ActivityResponse = read ?? ACTIVITY_QUIET;
  const said = activityIndicator(view.counts);
  const [open, setOpen] = useState(false);

  // Escape closes it, as it closes every other popover here. Nothing in it is a form, so nothing is lost by that.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  // A panel about work that has finished is a panel about nothing: it closes itself when the last row goes.
  useEffect(() => { if (!said) setOpen(false); }, [said]);

  if (!said) return null;
  const stopped = view.items.filter((i) => i.state === "stopped");
  const now = view.items.filter((i) => i.state === "running");
  const waiting = view.items.filter((i) => i.state === "waiting");

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`${said.label}. What Joogoos is doing`}
        aria-expanded={open}
        title="What Joogoos is doing"
        className={`flex items-center gap-1.5 h-8 pl-2 pr-2.5 rounded-full border text-xs font-semibold ${
          said.state === "stopped" ? "border-bad/30 bg-bad/5 text-bad" : "border-line bg-page text-ink-2 hover:border-line-2"
        }`}
      >
        {said.state === "working"
          ? <span aria-hidden><Logo size={18} thinking className="block" /></span>
          : <span className={`w-2 h-2 rounded-full ${said.state === "stopped" ? "bg-bad" : "bg-wait"}`} aria-hidden />}
        <span className="max-w-30 truncate">{said.label}</span>
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="region" aria-label="What Joogoos is doing"
            className="absolute right-0 top-10 z-50 w-80 max-w-[calc(100vw-1rem)] bg-panel border border-line rounded-2xl shadow-[0_14px_40px_rgba(23,23,26,.16)] overflow-hidden"
          >
            <Section title="Needs you" items={stopped} onGo={onGo} onClose={() => setOpen(false)} />
            <Section title="Now" items={now} onGo={onGo} onClose={() => setOpen(false)} />
            <Section title="Waiting" items={waiting} onGo={onGo} onClose={() => setOpen(false)} />
            <p className="px-3.5 py-2.5 border-t border-line text-[11px] text-muted">
              Everything here is running for this business. You can leave the page; it carries on.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Section({ title, items, onGo, onClose }: {
  title: string; items: ActivityItem[]; onGo: (item: ActivityItem) => void; onClose: () => void;
}) {
  if (!items.length) return null;
  return (
    <>
      <p className="px-3.5 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-wide text-faint">{title}</p>
      <ul>
        {items.map((item) => {
          const said = activitySaid(item);
          return (
            <li key={item.id}>
              <button
                onClick={() => { onClose(); onGo(item); }}
                disabled={!item.tab}
                className="w-full flex items-start gap-2.5 px-3.5 py-2 text-left hover:bg-page disabled:hover:bg-transparent disabled:cursor-default"
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                  item.state === "stopped" ? "bg-bad" : item.state === "waiting" ? "bg-wait" : "bg-good"
                }`} aria-hidden />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink truncate">{said.title}</span>
                  {said.detail ? <span className="block text-[11.5px] text-muted truncate">{said.detail}</span> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}
